import AppRouter, { getIndexRouteName, TABLE_ID_KEY } from '@/router/index';
import { Routes } from '@/router/Routes';
import FilemanagerAPI from '@/modules/filemanager/FilemanagerAPI';
import RegulationStore from '@/modules/regulation/services/store/RegulationStore';
import DealsApi from '@/modules/deals/services/DealsApi';
import { isNumber } from 'lodash-es';
import { ProductType } from '@/commons/enums/ProductType';
import { Dictionary, RawLocation } from 'vue-router/types/router';
import AuthService from '@/modules/user/AuthService';
import ClientAuthService from '@/modules/client/services/ClientAuthService';
import { FormSteps } from '@/components/calculator/FormSteps';
import { useCalculatorVersion } from '@/components/calculator/useCalculatorVersion';
import { BankSymbol } from '@/commons/dictionaries/BankSymbol';
import { FeedbackSourceType } from '@/modules/nps/enums/FeedbackSourceType';
import EnvironmentService from '@/env/EnvironmentService';
import { ProvisionGroupType } from '@/modules/provisions/models/ProvisionGroupType';
import processProxy from '@/env/processProxy';
import { useThemeCookie } from '@/composables/useThemeCookie';
import { useLanguageCookie } from '@/composables/useLanguageCookie';
import { resolveLendiAiBaseUrl } from '@/utils/lendiAiBaseUrl';
import { resolveLendiV3BaseUrl } from '@/utils/lendiV3BaseUrl';

type MultiformTestPanelQuery = Partial<{
  bankSymbol: BankSymbol;
}>;
interface Simulationable {
  productType: Nullable<ProductType>;
  simulationId?: Nullable<string>;
  id?: number;
}

class GoToService {
  public index() {
    AppRouter.push({
      name: getIndexRouteName(),
    });
  }

  public dashboard() {
    AppRouter.push({
      name: Routes.DASHBOARD,
    });
  }

  public branch(id: number) {
    AppRouter.push({
      name: Routes.BRANCHES.BRANCH_DETAILS,
      params: {
        id: String(id),
      },
    });
  }

  public partner(id: number) {
    AppRouter.push({
      name: Routes.PARTNERS.PARTNER_DETAILS,
      params: {
        id: String(id),
      },
    });
  }

  public client(id: number, tab: number = 0) {
    AppRouter.push({
      name: Routes.CLIENTS.CLIENT_DETAILS,
      params: {
        id: String(id),
      },
      query: {
        path: String(tab),
      },
    });
  }

  public user(id: number, tab: number = 0) {
    AppRouter.push({
      name: Routes.ADMIN.USERS_MANAGMENT_DETAILS,
      params: {
        id: String(id),
      },
      query: {
        tab: String(tab),
      },
    });
  }

  public userInNewTab(id: number, tab: number = 0) {
    const route = AppRouter.resolve({
      name: Routes.ADMIN.USERS_MANAGMENT_DETAILS,
      params: {
        id: String(id),
      },
      query: {
        tab: String(tab),
      },
    });
    window.open(route.href, '_blank');
  }

  public branchLocation(id: number): RawLocation {
    return { path: `/app/branches/${id}`, params: { id: String(id), }, };
  }

  public clientLocation(id: number, tab: number = 0): RawLocation {
    return { path: `/app/clients/${id}`, query: { path: String(tab), }, params: { id: String(id), }, };
  }

  public myCalendar() {
    AppRouter.push({
      name: Routes.ACTIVITIES.CALENDAR,
    });
  }

  public prospect(id: number, query?: Record<string, string>) {
    AppRouter.push({
      name: Routes.PROSPECT.DETAILS,
      params: {
        id: String(id),
      },
      query: query,
    });
  }

  public prospectList() {
    AppRouter.push({
      name: Routes.PROSPECT.LIST,
    });
  }

  public clients() {
    AppRouter.push({
      name: Routes.CLIENTS.CLIENT_LIST,
    });
  }

  public branches() {
    AppRouter.push({
      name: Routes.BRANCHES.BRANCHES_LIST,
    });
  }

  public partners() {
    AppRouter.push({
      name: Routes.PARTNERS.PARTNERS_LIST,
    });
  }

  public setQuery(query: Dictionary<string>, override = true) {
    const newQuery = override ? query : { ...AppRouter.currentRoute.query, ...query, };
    AppRouter.replace({ path: AppRouter.currentRoute.path, query: newQuery, });
  }

  public thirdPartySite(url: string, newTab = false) {
    if (newTab) {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
      return;
    }

    window.location.assign(url);
  }

  public simulationForm(
    type: ProductType,
    step: FormSteps = FormSteps.CREDIT_PARAMETERS,
    simulationId?: Nullable<string>,
    dealId?: number,
  ) {
    const query = {
      simulationId: simulationId ? String(simulationId) : undefined,
      dealId: dealId ? String(dealId) : undefined,
    };

    AppRouter.push({
      name: Routes.NEW_CALCULATOR.FORM(type, step),
      query,
    });
  }

  public simulationSummary(type: ProductType, simulationId: string, dealId?: number) {
    const query = { dealId: dealId ? String(dealId) : undefined, };
    AppRouter.push({
      name: Routes.NEW_CALCULATOR.SUMMARY(type),
      params: { simulationId, },
      query,
    });
  }

  public async simulation(deal: Simulationable) {
    const isSmeCalculator: boolean = deal.productType === ProductType.SME;
    if (!isSmeCalculator && useCalculatorVersion().isNewCalculatorAvailable()) {
      if (deal.simulationId) {
        this.simulationSummary(deal.productType!, deal.simulationId, deal.id);
      } else {
        this.simulationForm(deal.productType!, undefined, deal.simulationId, deal.id);
      }
    } else {
      AppRouter.push({
        name: deal.productType!,
        params: {
          id: deal.id ? String(deal.id) : '',
          simulationId: deal.simulationId ? String(deal.simulationId) : '',
        },
      });
    }
  }

  public dealLocation(id: number, step: string, tab: string = Routes.DEALS.DEAL_DETAILS_TAB.PREVIEW): RawLocation {
    const query = AppRouter.currentRoute.query || {};
    query.path = tab;
    const normalizedStep = !step || step === 'undefined' ? Routes.DEALS.WIZARD.CHOOSE_OFFERS : step;
    return { path: `/app/deals/${id}/${normalizedStep}`, query: query, params: { id: String(id), }, };
  }

  public deal(
    id: number,
    step: string = Routes.DEALS.WIZARD.CHOOSE_OFFERS,
    tab: string = Routes.DEALS.DEAL_DETAILS_TAB.PREVIEW,
    documentCategory?: string,
  ) {
    const query = AppRouter.currentRoute.query || {};
    query.path = tab;
    if (documentCategory) {
      query.documentCategory = documentCategory;
    }
    AppRouter.push({
      path: `/app/deals/${id}/${step || ''}`,
      query: query,
      params: {
        id: String(id),
      },
    });
  }

  private async getLoanApplicationRouteId(loanApplicationId: number, dealId: number): Promise<number> {
    const response = await DealsApi.getLoanApplicationsByDealId(dealId);
    return response.map((loanApplication) => loanApplication.id).findIndex((id) => id === loanApplicationId);
  }

  public async loanApplication(loanApplicationId: number, dealId: number, index?: number) {
    const param = isNumber(index) ? index : await this.getLoanApplicationRouteId(loanApplicationId, dealId);
    this.deal(dealId, `${Routes.DEALS.WIZARD.CREDIT_DETAILS}-${param}`);
  }

  /**
   * @Info Same resolver options as V3: `forceDev` is always true for lendi-ai base resolution
   * (local lendi-ai in DEV); pass `customBaseUrl` to test a lendi-ai Vercel preview branch.
   */
  public getLendiAiUrl(route: string, customBaseUrl?: string) {
    const baseUrl = resolveLendiAiBaseUrl({ forceDev: true, customBaseUrl });
    const time = new Date().getTime();

    const isLocal = processProxy.VUE_APP_ENV === 'DEV';
    const isVercel = processProxy.VUE_APP_VERCEL_ENV === 'preview';

    const timeQuery = `time=${time}`;
    const tokenQuerySeparator = route.includes('?') ? '&' : '?';

    /**
     * @Info
     * We are passing different tokens based on environment:
     * - oldToken if available and not already in route
     * - accessToken for vercel or local environments
     */
    const tokenQuery = AuthService.oldToken && !route.includes('authToken') ?
      `authToken=${AuthService.oldToken}` :
      AuthService.isZitadelAuthMode && AuthService.accessToken && (isVercel || isLocal)
        ? `authToken=${AuthService.accessToken}` :
        '';

    const queryParams = [timeQuery, tokenQuery].filter(Boolean).join('&');

    return `${baseUrl}/iframe/${route}${tokenQuerySeparator}${queryParams}`;
  }

  /**
   * @Info
   * baseUrl for Lendi CRM V3, fyi crm.qa.lendi.pl is a copy of master with qa environment,
   * - for developing both v3 and v2 iframes set forceDev to true and develop locally both apps, 
   * - for testing deployed changes use customBaseUrl to set a custom base url to open V3 vercel branch
   * VUE_APP_V3_BASE_URL_FOR_TESTING is a hardcoded base url for testing, it is set in vercel environment variables,
   * @Important is to clear customBaseUrl after finished tests and merging V3 PR to production, to not init missmatches with other devs
   * 
   */
  public getLendiV3Url(route: string, forceDev?: boolean, customBaseUrl?: string) {
    const baseV3Url = resolveLendiV3BaseUrl({ forceDev, customBaseUrl });
    const isLocal = processProxy.VUE_APP_ENV === 'DEV';
    const { isVercelHosted } = EnvironmentService.Environment;
    const isVercel = processProxy.VUE_APP_VERCEL_ENV === 'preview' || isVercelHosted;
    const tokenQuerySeparator = route.includes('?') ? '&' : '?';
    /**
     * @Info
     * We are passing mfaToken for mfa flow only on vercel preview environment, and preprod(qa and demo) environment because we can have domain missmatches,
     * and the accessToken cookie will not be visible between two domains that are not .lendi.pl
     */
    let tokenQuery = AuthService.oldToken && !route.includes('authToken') ?
      `${tokenQuerySeparator}authToken=${AuthService.oldToken}` :
      AuthService.isZitadelAuthMode && AuthService.accessToken && (isVercel || isLocal)
        ? `${tokenQuerySeparator}mfaToken=${AuthService.accessToken}` : '';

    if (isVercel) {
      const theme = useThemeCookie().value ?? '';
      const language = useLanguageCookie().value ?? '';

      if (theme) {
        const separator = tokenQuery ? '&' : tokenQuerySeparator;
        tokenQuery += `${separator}theme=${encodeURIComponent(theme)}`;
      }

      if (language) {
        const separator = tokenQuery ? '&' : tokenQuerySeparator;
        tokenQuery += `${separator}language=${encodeURIComponent(language)}`;
      }
    }
    
    return `${baseV3Url}/${route}${tokenQuery}`;
  }

  public lendiV3(route: string) {
    window.location.href = this.getLendiV3Url(route);
  }

  public deals() {
    AppRouter.push({
      name: Routes.DEALS.DEALS_LIST,
    });
  }

  public kanbanDeals() {
    AppRouter.push({
      name: Routes.DEALS.KANBAN_DEALS_LIST,
    });
  }

  public issue(title: string, category: string) {
    AppRouter.push({
      name: Routes.ISSUES.ISSUES_QUERY,
      params: {
        category: category,
        query: title,
      },
    });
  }

  public file(path: string, isFolder: boolean) {
    if (isFolder) {
      AppRouter.push({
        name: Routes.FILEMANAGER.FILEMANAGER,
        params: {
          path: path,
        },
      });
    } else {
      // Endpoint pobierania wymaga nagłówka Authorization, którego window.open() nie przekaże.
      FilemanagerAPI.downloadWithAuthorization(path);
    }
  }

  public editUser(tab: string = '1') {
    AppRouter.push({
      name: Routes.USER_EDIT,
      query: {
        tab: tab,
      },
    });
  }

  public login() {
    if (AuthService.isLoggedIn) {
      AuthService.softLogout();
      return;
    }
    AppRouter.push({
      name: Routes.PASSWORD_MANAGEMENT.LOGIN,
    });
  }

  public register() {
    AppRouter.push({
      name: Routes.REGISTER,
    });
  }

  public bhp() {
    AppRouter.push({
      name: Routes.BHP_TEST.START_BHP_TEST,
    });
  }

  public knf() {
    AppRouter.push({
      name: Routes.KNF_TEST.START_KNF_TEST,
    });
  }

  public stmt() {
    AppRouter.push({
      name: Routes.STATEMENTS_TEST.START_STATEMENTS_TEST,
    });
  }

  public ethic() {
    AppRouter.push({
      name: Routes.ETHIC_QUIZZ.START_ETHIC_QUIZZ,
    });
  }

  public bankStmt() {
    AppRouter.push({
      name: Routes.BANK_STMTS_QUIZ.START_BANK_STMTS_QUIZ,
    });
  }

  public discussionList() {
    AppRouter.push({
      name: Routes.DISCUSSIONS.DISCUSSIONS_LIST,
    });
  }

  public discussion(id: number) {
    AppRouter.push({
      name: Routes.DISCUSSIONS.DISCUSSIONS_DETAILS,
      params: {
        id: String(id),
      },
    });
  }

  public regulation(id: number): void {
    RegulationStore.setRegulationId(id);
    AppRouter.replace(this.getRegulationRouteObject(id));
  }

  public getRegulationRouteObject(id: number): RawLocation {
    return { name: Routes.REGULATIONS.REGULATION_DETAILS, params: { id: String(id), }, };
  }

  public regulationList() {
    AppRouter.push({
      name: Routes.REGULATIONS.REGULATION_LIST,
    });
  }

  public addNewRegulation() {
    AppRouter.push({
      name: Routes.REGULATIONS.ADD_REGULATION,
    });
  }

  public division(id: number) {
    AppRouter.push({
      name: Routes.DIVISIONS.DIVISIONS_DETAILS,
      params: {
        id: String(id),
      },
    });
  }

  public Eko(url: string) {
    ClientAuthService.goToEKO(url);
  }

  public getProcedureRouteObject(id: number): RawLocation {
    return { name: Routes.PROCEDURES.PROCEDURES_DETAILS, params: { id: String(id), }, };
  }

  public procedure(id: number) {
    AppRouter.push(this.getProcedureRouteObject(id));
  }

  public proceduresList() {
    AppRouter.push({
      name: Routes.PROCEDURES.PROCEDURES_LIST,
    });
  }

  public permissionsList() {
    AppRouter.push({
      name: Routes.ACCREDITATION_PANEL.PERMISSIONS_BANKS,
    });
  }

  public compliance(query: Record<string, any>) {
    AppRouter.push({
      name: Routes.ACCREDITATION_PANEL.COMPLIANCE,
      query,
    });
  }

  public quizzesPanel(query: Record<string, any>) {
    AppRouter.push({
      name: Routes.ACCREDITATION_PANEL.QUIZZES_PANEL,
      query,
    })
  }

  public lendiTeam(department?: string) {
    AppRouter.push({
      name: Routes.ABOUT,
      ...(department ? {query: {department,},} : {}),
    });
  }

  public hrLocked() {
    AppRouter.push({
      name: Routes.HR_LOCKED,
    });
  }

  public teleconference(uuid: string) {
    if (EnvironmentService.Environment.isAppDomain().NESTO_RO) {
      window.open(`${processProxy.VUE_APP_LENDI_B2C_URL}imobiliare/teleconference/${uuid}`, '_blank');
    } else {
      const route = AppRouter.resolve({
        name: Routes.TELECONFERENCE,
        params: { uuid, },
        query: {
          firstName: AuthService.User?.firstName,
          lastName: AuthService.User?.lastName,
        },
      });
      window.open(route.href, '_blank');
    }
  }

  public getProvisionPrintPreviewRoute(tableUuid: string, productType: ProductType, groupType: ProvisionGroupType) {
    const route = AppRouter.resolve({
      name: Routes.PROVISION.PRINT,
      params: {
        [TABLE_ID_KEY]: tableUuid,
        productType: productType,
      },
      query: {
        type: groupType,
      },
    });
    window.open(route.href, '_blank');
  }

  public multiformTestPanel(query: MultiformTestPanelQuery = {}) {
    AppRouter.push({
      name: Routes.ADMIN.MULTIFORM_TEST_PANEL,
      query,
    });
  }

  public multiformEnginePreview(id: string) {
    const route = AppRouter.resolve({
      name: Routes.MULTIFORM_ENGINE_PREVIEW,
      params: {
        id,
      },
    });
    window.open(route.href, '_blank');
  }

  public multiformEngineTest(id: string) {
    AppRouter.push({
      name: Routes.ADMIN.MULTIFORM_ENGINE_TEST,
      params: {
        id: String(id),
      },
    });
  }

  public goBack() {
    AppRouter.back();
  }

  public survey(surveyUuid: string, sourceType: FeedbackSourceType = FeedbackSourceType.MEET) {
    window.open(`${processProxy.VUE_APP_LENDI_B2C_URL}ankieta?uuid=${surveyUuid}&source_type=${sourceType}`, '_self');
  }
  public realEstateOffersListingWithDeal(dealId: number) {
    AppRouter.push({
      name: Routes.REAL_ESTATES_LISTING.REAL_ESTATE_OFFERS_DEAL,
      params: {
        dealId: String(dealId),
      },
    });
  }

  public realEstatesListing() {
    AppRouter.push({
      name: Routes.REAL_ESTATES_LISTING.REAL_ESTATE_OFFERS,
    });
  }
}

export default new GoToService();
